<!--
  A page to show all the skills
-->

<template>
  <div v-loading="state.loading" class="skills-list">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item>Home</el-breadcrumb-item>
    </el-breadcrumb>
    <main-title>Skills</main-title>
    <view-selector
      :view="view"
      :compare="compare"
      @update="changeView"
      :force="true"></view-selector>
    <!-- skill creation popup -->
    <skill-editor
      :visible.sync="creating"
      @input="save"
      :existing="viewList"></skill-editor>
    <!-- public skills -->
    <div v-if="state.user && state.user.role === 'admin'">
      <subtitle id="section-public">Public skills</subtitle>
      <ul>
        <!-- global items link -->
        <li style="margin-bottom: 0.5em">
          <span class="skills-list-main"
            v-id="'global-skill'">
            <span class="skills-list-count">&nbsp;-</span>
            <router-link
              :to="{name: 'global', query: $route.query}"
              class="skills-list-name skills-list-global"
              >Global item</router-link>
          </span>
        </li>
        <!-- all skills -->
        <li v-for="skill in listSkills.filter(x => !x.first.owner)" :key="skill.skill">
          <span class="skills-list-main"
            v-id="'skills-' + skill.skill">
            <span class="skills-list-count"
              >({{ skill.first.intentsCount || '-' }})</span>
            <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
              <i :class="skill.first.status==='deleted'?'el-icon-circle-close':skill.first.status==='created'?'el-icon-circle-plus-outline':skill.first.status==='edited'?'el-icon-warning-outline':'el-icon-circle-check'"></i>
              <template slot="content">
                {{skill.first.explanation}}
              </template>
            </el-tooltip>
            <!-- view version (or compare if not in view) -->
            <router-link
              :to="{name: 'skill', params: {skill: skill.skill}, query: skill.query}"
              class="skills-list-name skills-list-skill"
              :class="'skills-list-' + skill.first.source"
              >{{ skill.first.name }}</router-link>
            <!-- compare version if different from view -->
            <router-link v-if="skill.second"
              :to="{name: 'skill', params: {skill: skill.skill}, query: skill.query}"
              class="skills-list-name skills-list-skill"
              :class="'skills-list-' + skill.second.source"
              >{{ skill.second.name }}</router-link>
          </span>
        </li>
        <!-- new skill -->
        <li style="margin-top: 0.5em">
          <span class="skills-list-main">
            <span class="skills-list-count">&nbsp;+</span>
            <a class="skills-list-name skills-list-add"
              href="#" @click.prevent="createSkill">
              <em>New skill</em>
            </a>
          </span>
        </li>
      </ul>
    </div>
    <!-- owned skills -->
    <div v-if="state.user && state.user.role === 'admin'">
      <subtitle id="section-owned">Owned skills</subtitle>
      <ul>
        <!-- all skills -->
        <li v-for="skill in listSkills.filter(x => x.first.owner)" :key="skill.skill">
          <span class="skills-list-main"
            v-id="'skills-' + skill.skill">
            <span class="skills-list-count"
              >({{ skill.first.intentsCount || '-' }})</span>
            <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
              <i :class="skill.first.status==='deleted'?'el-icon-circle-close':skill.first.status==='created'?'el-icon-circle-plus-outline':skill.first.status==='edited'?'el-icon-warning-outline':'el-icon-circle-check'"></i>
              <template slot="content">
                {{skill.first.explanation}}
              </template>
            </el-tooltip>
            <!-- view version (or compare if not in view) -->
            <router-link
              :to="{name: 'skill', params: {skill: skill.skill}, query: skill.query}"
              class="skills-list-name skills-list-skill"
              :class="'skills-list-' + skill.first.source"
              >{{ skill.first.name }}</router-link>
            <!-- compare version if different from view -->
            <router-link v-if="skill.second"
              :to="{name: 'skill', params: {skill: skill.skill}, query: skill.query}"
              class="skills-list-name skills-list-skill"
              :class="'skills-list-' + skill.second.source"
              >{{ skill.second.name }}</router-link>
          </span>
          <span v-if="clients"
            class="skills-list-infos">
            <router-link v-if="clients[skill.first.owner]"
              :to="{name: 'user', params: {user: skill.first.owner}}"
              class="skills-list-owner">
              {{ clients[skill.first.owner].email }}
            </router-link>
            <el-tooltip v-else
              placement="top" effect="light" :enterable="false" :tabindex="-1">
              <i class="el-icon-error"></i>
              <template slot="content">
                <em>owner</em> not found <strong>{{skill.first.owner}}</strong>
              </template>
            </el-tooltip>
          </span>
        </li>
      </ul>
    </div>
    <!-- personal skills -->
    <div v-if="state.user && state.user.role === 'client'">
      <subtitle id="section-yours">Your skills</subtitle>
      <ul>
        <!-- all skills -->
        <li v-for="skill in listSkills" :key="skill.skill">
          <span class="skills-list-main"
            v-id="'skills-' + skill.skill">
            <span class="skills-list-count"
              >({{ skill.first.intentsCount || '-' }})</span>
            <el-tooltip placement="top" effect="light" :enterable="false" :tabindex="-1">
              <i :class="skill.first.status==='deleted'?'el-icon-circle-close':skill.first.status==='created'?'el-icon-circle-plus-outline':skill.first.status==='edited'?'el-icon-warning-outline':'el-icon-circle-check'"></i>
              <template slot="content">
                {{skill.first.explanation}}
              </template>
            </el-tooltip>
            <!-- view version (or compare if not in view) -->
            <router-link
              :to="{name: 'skill', params: {skill: skill.skill}, query: skill.query}"
              class="skills-list-name skills-list-skill"
              :class="'skills-list-' + skill.first.source"
              >{{ skill.first.name }}</router-link>
            <!-- compare version if different from view -->
            <router-link v-if="skill.second"
              :to="{name: 'skill', params: {skill: skill.skill}, query: skill.query}"
              class="skills-list-name skills-list-skill"
              :class="'skills-list-' + skill.second.source"
              >{{ skill.second.name }}</router-link>
          </span>
        </li>
        <!-- new skill -->
        <li style="margin-top: 0.5em">
          <span class="skills-list-main">
            <span class="skills-list-count">&nbsp;+</span>
            <a class="skills-list-name skills-list-add"
              href="#" @click.prevent="createSkill">
              <em>New skill</em>
            </a>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { comparedItems } from '../../utils/compare.js'
import State from '../../stores/state.js'
import ViewSelector from '../ViewSelector.vue'
import SkillEditor from './SkillEditor.vue'

export default {
  components: {
    ViewSelector,
    SkillEditor,
  },
  props: {
    view: String,
    compare: String,
  },
  data () {
    return {
      state: State.state,
      viewList: [], // view skills
      compareList: null, // compare skills
      clients: null, // all client users
      creating: false, // open the skill creation popup
    }
  },
  computed: {
    // create a sorted compared list of skills
    listSkills () {
      let skills = comparedItems('skill',
        this.viewList, this.compareList, this.$route.query)

      let data = []
      for (let skill in skills) {
        data.push(skills[skill])
      }
      data.sort((a, b) => (a.first.name > b.first.name) -
        (a.first.name < b.first.name))
      return data
    },
  },
  inject: ['onerror'],
  // load everything
  created () {
    this.loadView()
    this.loadCompare()
    this.loadClients()
  },
  watch: {
    // user changed, reload everything
    'state.user' () {
      this.loadView()
      this.loadCompare()
      this.loadClients()
    },
    // load view skills
    view () { this.loadView() },
    // load compare skills
    compare () { this.loadCompare() },
  },
  methods: {
    // load view skills
    loadView () {
      let view = this.view
      State.listSkills(view)
        .then(data => {
          if (view !== this.view) return
          this.viewList = data.skills
        }).catch(this.onerror)
    },
    // load compare skills
    loadCompare () {
      let view = this.compare
      if (!view) {
        this.compareObj = null
        return
      } else {
        State.listSkills(view)
          .then(data => {
            if (view !== this.compare) return
            this.compareList = data.skills
          }).catch(this.onerror)
      }
    },
    // load the list of client users
    loadClients () {
      if (!this.state.user || this.state.user.role !== 'admin') {
        this.clients = null
        return
      }
      State.listUsers().then(data => {
        let clients = {}
        for (let client of data.users.filter(x => x.role === 'client')) {
          clients[client.user] = client
        }
        this.clients = clients
      }).catch(error => {
        if (error.status === 403) this.clients = null
        else this.onerror(error)
      })
    },
    // change view and compare
    changeView (view, compare) {
      this.$router.push({ query: { view: view, compare: compare } })
    },
    // open the skill creation popup
    createSkill () {
      this.creating = true
    },
    // create a new skill
    save (value) {
      State.createSkill(value, value.skill).then(data => {
        this.$message({
          message: 'Skill created',
          type: 'success',
        })
        this.$router.push({ name: 'skill', params: { skill: data.skill } })
      }).catch(this.onerror)
    },
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";

  .skills-list-name {
    color: $--color-text-regular;
    /*font-weight: bold;*/
    text-decoration: none;
    margin-left: 5px
  }
  .skills-list-name:hover {
    text-decoration: underline;
  }
  .skills-list-add {
    font-style: italic;
  }
  .skills-list-new {
    background-color: $--color-primary-lighter;
  }
  .skills-list-old {
    background-color: $--color-warning-lighter;
  }
  .skills-list-main {
    display: inline-block;
    min-width: 200px;
    margin-left: -200px;

    .el-icon-circle-check {
      color: $--color-success;
    }
    .el-icon-circle-plus-outline {
      color: $--color-primary;
    }
    .el-icon-warning-outline {
      color: $--color-primary;
    }
    .el-icon-circle-close {
      color: $--color-warning;
    }
  }
  .skills-list-infos {
    .el-icon-error {
      color: $--color-danger;
    }
  }
  .skills-list-count {
    font-family: monospace;
    display: inline-block;
    width: 40px;
  }
  .skills-list ul {
    color: $--color-text-placeholder;
    list-style-type: none;
  }
  .skills-list li {
    margin-top: 0.2em;
    margin-bottom: 0.2em;
    margin-left: 200px;
  }
  .skills-list-owner {
    color: $--color-text-regular;
    /*font-weight: bold;*/
    text-decoration: none;
  }
  .skills-list-owner:hover {
    text-decoration: underline;
  }
</style>
